'use client';
import React, { useEffect, useContext, useState } from 'react';
import { useActor } from '@xstate/react';
import { GlobalStateContext } from '../../state/app/index';
import logger from '../../logger';

const LoadingOrSyncing = ({ statusText = 'Loading' }) => {
  return (
    <main
      className="fixed inset-0 flex flex-col items-center justify-center p-6 overflow-hidden"
      style={{ background: 'radial-gradient(circle at center, #121a25 0%, #0a0e14 100%)', zIndex: 10000, width: '100%', height: '100%' }}
    >
      {/* Center Progress Indicator Cluster */}
      <div className="relative flex flex-col items-center gap-10 z-10">
        {/* Branding */}
        <div className="flex flex-col items-center opacity-30">
          <span className="font-['Manrope',sans-serif] font-extrabold text-[#d9e6fd] tracking-[-0.04em] text-lg">
            Drynotes
          </span>
          <span className="font-['Inter',sans-serif] text-[9px] tracking-[0.3em] text-[#9facc1] uppercase mt-1">
            Don&apos;t repeat yourself, stay focused, and find what matters
          </span>
        </div>

        {/* Architectural Progress Indicator */}
        <div className="relative w-24 h-24 flex items-center justify-center">
          {/* Outer Track */}
          <div className="absolute inset-0 rounded-full border-[3px] border-[#1a2637] opacity-20"></div>
          {/* Inner Active Spinner */}
          <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-[#9ecaff] animate-[spin_2s_linear_infinite]"></div>
          {/* Subtle Ambient Glow */}
          <div className="absolute inset-0 rounded-full bg-[#9ecaff]/5 blur-xl"></div>
          {/* Minimal Icon Anchor */}
          <span className="material-symbols-outlined text-[#9ecaff]/40 text-2xl font-light">
            architecture
          </span>
        </div>

        {/* Loading Content */}
        <div className="flex flex-col items-center gap-3">
          <p className="font-['Manrope',sans-serif] font-semibold text-[#9facc1] tracking-wider text-sm opacity-80 uppercase">
            {statusText}
          </p>
          {/* Progress Metadata */}
          <div className="flex items-center gap-4 mt-2">
            <div className="h-[1px] w-12 bg-[#3c495b] opacity-20"></div>
            <span className="font-['Inter',sans-serif] text-[10px] tracking-[0.15em] text-[#6a768a] uppercase">
              Editorial Intelligence
            </span>
            <div className="h-[1px] w-12 bg-[#3c495b] opacity-20"></div>
          </div>
        </div>
      </div>

      {/* Decorative Background Elements */}
      <div className="fixed top-[-10%] right-[-5%] w-[40vw] h-[40vw] bg-[#9ecaff]/5 blur-[120px] rounded-full pointer-events-none z-0"></div>
      <div className="fixed bottom-[-10%] left-[-5%] w-[30vw] h-[30vw] bg-[#1a2637]/10 blur-[100px] rounded-full pointer-events-none z-0"></div>
    </main>
  );
};

const AppLoadingPage = (props) => {
  const globalServices = useContext(GlobalStateContext);
  
  // Context should always be available since GlobalStateProvider wraps Routes
  // But guard against SSR or edge cases - show loading while context initializes
  if (!globalServices?.appState) {
    return <LoadingOrSyncing statusText="Initializing Application..." />;
  }
  
  // Hooks must be called unconditionally - service should always exist here
  const [state, send] = useActor(globalServices.appState);
  const [loadingState, setLoadingState] = useState({
    statusText: 'Initializing Application...',
  });

  // Update loading state based on application state
  useEffect(() => {
    if (!state?.value) return;

    logger.info('AppLoadingPage state change:', state.value);

    let statusText = 'Loading...';

    // Handle different state types
    if (typeof state.value === 'string') {
      // Simple string state
      switch (state.value) {
        case 'initalize':
          statusText = 'Initializing Application...';
          break;
        case 'validateLoggedInStateAPI':
          statusText = 'Validating Authentication...';
          break;
        case 'validateRequiredAppData':
          statusText = 'Validating Application Data...';
          break;
        case 'primeApplication':
          statusText = 'Preparing Application...';
          break;
        case 'applicationReady':
          statusText = 'Application Ready!';
          break;
        case 'loggedOut':
          statusText = 'Redirecting to Login...';
          break;
        case 'ERROR':
          statusText = 'Application Error...';
          break;
        default:
          statusText = `Loading... (${state.value})`;
      }
    } else if (typeof state.value === 'object') {
      // Object state (parallel states like fetchRequiredAppData)
      const stateKeys = Object.keys(state.value);
      if (stateKeys.length > 0) {
        const currentState = stateKeys[0];
        logger.info(
          'Parallel state detected:',
          currentState,
          state.value[currentState]
        );

        switch (currentState) {
          case 'fetchRequiredAppData':
            // Check which parallel state is active
            const parallelStates = state.value.fetchRequiredAppData;
            if (parallelStates) {
              // Determine which data is being fetched
              if (
                parallelStates.userSettings &&
                parallelStates.userSettings !== 'REQUIRED_DATA_READY'
              ) {
                statusText = 'Loading User Settings...';
              } else if (
                parallelStates.areasOfFocusCollections &&
                parallelStates.areasOfFocusCollections !== 'REQUIRED_DATA_READY'
              ) {
                statusText = 'Loading Areas of Focus...';
              } else if (
                parallelStates.notes &&
                parallelStates.notes !== 'REQUIRED_DATA_READY'
              ) {
                statusText = 'Loading Notes...';
              } else if (
                parallelStates.notebooks &&
                parallelStates.notebooks !== 'REQUIRED_DATA_READY'
              ) {
                statusText = 'Loading Notebooks...';
              } else if (
                parallelStates.userPanelConfigs &&
                parallelStates.userPanelConfigs !== 'REQUIRED_DATA_READY'
              ) {
                statusText = 'Loading Panel Configuration...';
              } else if (
                parallelStates.chromeModels &&
                parallelStates.chromeModels !== 'REQUIRED_DATA_READY'
              ) {
                statusText = 'Loading Interface Layout...';
              } else {
                statusText = 'Fetching Required Data...';
              }
            } else {
              statusText = 'Fetching Required Data...';
            }
            break;
          default:
            statusText = `Loading... (${currentState})`;
        }
      }
    }

    // Only update if the status text has changed
    if (loadingState.statusText !== statusText) {
      logger.info(`Loading state changed to: ${statusText}`);
      setLoadingState({ statusText });
    }
  }, [state.value, loadingState.statusText]);

  // Show loading page until the application is fully ready
  // Only show if activeFlexLayoutModel is null AND app is not primed
  // This prevents loading screen from showing during URL updates when app is already ready
  const isAppReady = state?.context?.applicationPrimed === true;
  const hasFlexLayoutModel = state?.context?.activeFlexLayoutModel !== null;
  
  if (!hasFlexLayoutModel && !isAppReady) {
    return <LoadingOrSyncing statusText={loadingState.statusText} />;
  }
  return null;
};

export default AppLoadingPage;
